// Temporary Disarm — shared constants & helpers (End User Web)
// Per-partition "disarm for a fixed period, then auto re-arm". Mirrors Camera
// Bypass but keyed by partitionId. NO backend yet — the tempDisarm store module
// holds mock data. Time helpers are reused from the bypass model.

import { formatCountdown, formatRemaining, formatApprovalTimeout, formatDurationLabel, parseServerTime, isWindowEditable } from '@/models/bypass'

export { formatCountdown, formatRemaining, formatApprovalTimeout, isWindowEditable }

// End-user duration options (PRD §6.3: 15 / 30 min / 1h / 2h).
export const TEMP_DISARM_DURATIONS = [
  { key: '15min', label: '15 min', ms: 15 * 60 * 1000 },
  { key: '30min', label: '30 min', ms: 30 * 60 * 1000 },
  { key: '1h', label: '1 hour', ms: 60 * 60 * 1000 },
  { key: '2h', label: '2 hours', ms: 2 * 60 * 60 * 1000 },
]

export function getTempDisarmDuration (key) {
  return TEMP_DISARM_DURATIONS.find(d => d.key === key) || null
}

// Resolve a selected duration into concrete { endTime, label }. Pure.
export function resolveTempDisarmDuration (key, startTime) {
  const d = getTempDisarmDuration(key)
  const ms = d ? d.ms : TEMP_DISARM_DURATIONS[0].ms
  return { endTime: startTime + ms, label: d ? d.label : '' }
}

// Short status text for a partition-card tag given the current time.
export function tempDisarmStatusText (record, now) {
  if (!record) return ''
  if (record.status === 'awaiting') return 'Awaiting'
  const remaining = record.endTime - now
  if (remaining <= 0) return 'Re-arming'
  return formatRemaining(remaining)
}

// Longer, human descriptive text (list rows / dialogs).
export function tempDisarmDetailText (record, now) {
  if (!record) return ''
  if (record.status === 'awaiting') return 'Awaiting approval'
  const remaining = record.endTime - now
  if (remaining <= 0) return 'Re-arming…'
  return `Re-arms in ${formatRemaining(remaining)}`
}

// ── API ↔ store mapping (real backend, group ② site-api) ─────────────────────
// Temp disarm has no dedicated list endpoint; active state comes from the
// partition arming-status (TempDisarm schema) and pending from approval-requests.

// API TempDisarm (partition arming-status) → active record keyed by partitionId.
// siteId is stamped from the known request site & coerced to String so it matches
// the string selectedSiteId used by the site-scoped getters. (#1)
export function mapTempDisarm (td, siteId) {
  if (!td) return null
  const sid = siteId != null ? siteId : td.site_id
  return {
    partitionId: td.partition_id,
    siteId: sid != null ? String(sid) : null,
    status: td.is_temp_disarmed ? 'active' : 'ended',
    startTime: parseServerTime(td.requested_at),
    endTime: parseServerTime(td.expires_at),
    durationKey: null,
    durationLabel: td.duration ? formatDurationLabel(td.duration * 1000) : '',
    initiator: 'operator',
    initiatorName: td.requested_by_label || 'Central Station',
    reason: '',
    requestExpiresAt: null,
    raw: td,
  }
}

// site-api GET /arming-status partition → active temp-disarm record. This is the
// source that survives a page reload (create/approve/MQTT only populate it in
// session). The site-api shape is minimal — only is_temp_disarmed +
// temp_disarm_expires_at — so we key on those (NOT the richer mapTempDisarm
// fields, which the arming-status payload doesn't carry). Returns null when the
// partition isn't temp-disarmed. (spec §3.4 #32)
export function mapArmingStatusTempDisarm (p, siteId) {
  if (!p || !p.is_temp_disarmed) return null
  const sid = siteId != null ? siteId : p.site_id
  return {
    partitionId: p.partition_id,
    siteId: sid != null ? String(sid) : null,
    status: 'active',
    startTime: null,
    endTime: parseServerTime(p.temp_disarm_expires_at),
    durationKey: null,
    durationLabel: '',
    initiator: 'operator',
    initiatorName: p.temp_disarming_user_email || 'Central Station',
    reason: '',
    requestExpiresAt: null,
    raw: p,
  }
}

// API approval_request (action=temp_disarm) → an 'awaiting' record keyed by partitionId.
// siteId is stamped from the known request site (see mapTempDisarm). (#1)
export function mapTempDisarmApprovalRequest (ar, siteId) {
  if (!ar) return null
  const isApp = ar.requested_by_type === 'app_user'
  const durMs = (ar.requested_duration || 0) * 1000
  const sid = siteId != null ? siteId : ar.site_id
  return {
    partitionId: ar.partition_id,
    siteId: sid != null ? String(sid) : null,
    status: 'awaiting',
    startTime: parseServerTime(ar.created_at),
    endTime: null,
    durationKey: null,
    durationLabel: durMs ? formatDurationLabel(durMs) : '',
    initiator: isApp ? 'app user' : 'operator',
    initiatorName: ar.requested_by_label || 'Central Station',
    reason: ar.reason_label || '',
    approvalRequestId: ar.id,
    requestExpiresAt: parseServerTime(ar.expires_at),
    raw: ar,
  }
}

// API approval_request (action=arm | disarm) → an 'awaiting' record keyed by
// partitionId. Arm/disarm carry no duration or mode — the approval just gates the
// partition arm/disarm, which the server applies (followThroughApprovedArming) on
// approve. Kept separate from temp-disarm records.
export function mapArmingApprovalRequest (ar, siteId) {
  if (!ar) return null
  const isApp = ar.requested_by_type === 'app_user'
  const sid = siteId != null ? siteId : ar.site_id
  return {
    partitionId: ar.partition_id,
    siteId: sid != null ? String(sid) : null,
    status: 'awaiting',
    action: ar.action, // 'arm' | 'disarm'
    startTime: parseServerTime(ar.created_at),
    initiator: isApp ? 'app user' : 'operator',
    initiatorName: ar.requested_by_label || 'Central Station',
    reason: ar.reason_label || '',
    approvalRequestId: ar.id,
    requestExpiresAt: parseServerTime(ar.expires_at),
    raw: ar,
  }
}

// Picker duration key → seconds for the API create body.
export function tempDisarmDurationSeconds (key) {
  const d = getTempDisarmDuration(key)
  const ms = d ? d.ms : TEMP_DISARM_DURATIONS[0].ms
  return Math.round(ms / 1000)
}

// Attribution label for who initiated the temporary disarm.
export function tempDisarmInitiatorLabel (record) {
  if (!record) return ''
  if (record.initiator === 'operator') return record.initiatorName || 'Central Station'
  return record.initiatorName || 'You'
}
